import { postLocalJson } from "./localJsonTransport";

export interface ActorProfile {
  readonly actorId: string;
  readonly displayName: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly phoneE164: string | null;
  readonly contactEmail: string | null;
  readonly neucleosEmail: string | null;
  /** The opaque Actor-custody reference of the photo (US-CAN-08), or null. */
  readonly avatar: string | null;
  /** A short-lived signed URL for the photo; re-read before `avatarUrlExpiresAt`. */
  readonly avatarUrl: string | null;
  readonly avatarUrlExpiresAt: number | null;
  readonly timeZone: string | null;
  readonly locale: string | null;
  readonly updatedAt: number;
  readonly isSelf: boolean;
}

export interface ActorProfilePatch {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly phoneE164?: string | null;
  readonly contactEmail?: string | null;
  /** Only `null` travels this way: it removes the photo. Setting one is the upload lane. */
  readonly avatar?: null;
}

export type ProfileOutcome =
  | { readonly ok: true; readonly profile: ActorProfile }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

async function decode(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const value: unknown = await response.json();
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function nullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function parseProfile(value: unknown): ActorProfile | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  for (const key of [
    "displayName",
    "firstName",
    "lastName",
    "phoneE164",
    "contactEmail",
    "neucleosEmail",
    "avatar",
    "timeZone",
    "locale",
  ]) {
    if (!nullableString(row[key])) return null;
  }
  if (
    typeof row["actorId"] !== "string" ||
    typeof row["updatedAt"] !== "number" ||
    typeof row["isSelf"] !== "boolean"
  )
    return null;
  // A door older than the photo widening answers no URL; read that as none.
  const avatarUrl = row["avatarUrl"] ?? null;
  const avatarUrlExpiresAt = row["avatarUrlExpiresAt"] ?? null;
  if (!nullableString(avatarUrl)) return null;
  if (avatarUrlExpiresAt !== null && typeof avatarUrlExpiresAt !== "number") return null;
  return { ...row, avatarUrl, avatarUrlExpiresAt } as unknown as ActorProfile;
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<ProfileOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decode(response);
  if (!response.ok || body?.["ok"] !== true) {
    return {
      ok: false,
      kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
      reason: typeof body?.["reason"] === "string" ? body["reason"] : "The profile is unavailable.",
    };
  }
  const profile = parseProfile(body["profile"]);
  return profile
    ? { ok: true, profile }
    : { ok: false, kind: "transport", reason: "The profile door returned an invalid profile." };
}

export function readActorProfile(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ProfileOutcome> {
  return call("/api/neucleos/profile", { worldKey }, fetchImpl);
}

export function saveActorProfile(
  worldKey: string,
  patch: ActorProfilePatch,
  fetchImpl: typeof fetch = fetch,
): Promise<ProfileOutcome> {
  return call("/api/neucleos/profile/save", { worldKey, ...patch }, fetchImpl);
}

/**
 * US-CAN-08 — the one-gesture photo upload: the lane stores the square PNG
 * under the Actor's own prefix, sets the reference, and answers the profile
 * read back with its signed URL.
 */
export function uploadActorAvatar(
  worldKey: string,
  pngBase64: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ProfileOutcome> {
  return call("/api/neucleos/profile/avatar", { worldKey, pngBase64 }, fetchImpl);
}
